<!-- Toast Messages -->
<p-toast data-testid="bpmn-edit-toast"></p-toast>

<!-- BPMN Edit Container -->
<div
  class="bpmn-edit-container h-screen flex flex-col overflow-hidden"
  data-testid="bpmn-edit-page"
>
  <!-- Header with form and action buttons -->
  <div class="sticky top-0 bg-white z-10 px-6 pt-6 border-b border-gray-200">
    <div class="flex justify-between items-start mb-4">
      <div class="flex-1">
        <h2 class="text-2xl font-semibold text-gray-800 mb-4">
          {{ "bpmn.edit.title" | translate }}
        </h2>

        <!-- Process Information Form -->
        <form
          [formGroup]="processForm"
          class="space-y-3"
          data-testid="bpmn-edit-form"
        >
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl">
            <!-- Process Name Field -->
            <div class="space-y-2">
              <label
                for="processName"
                class="block text-sm font-medium text-gray-700"
              >
                {{ "bpmn.create.form.process_name" | translate }}
                <span class="text-red-500">*</span>
              </label>
              <input
                data-testid="bpmn-edit-process-name-input"
                id="processName"
                type="text"
                pInputText
                formControlName="processName"
                [placeholder]="
                  'bpmn.create.form.process_name_placeholder' | translate
                "
                class="w-full"
                [class.p-invalid]="hasFieldError('processName')"
              />
              <div class="h-5">
                @if (hasFieldError("processName")) {
                  <small class="text-red-500 block">
                    {{ getFieldErrorMessage("processName") }}
                  </small>
                }
              </div>
            </div>

            <!-- Process Definition ID Field -->
            <div class="space-y-2">
              <label
                for="processDefineId"
                class="block text-sm font-medium text-gray-700"
              >
                {{ "bpmn.create.form.process_define_id" | translate }}
                <span class="text-red-500">*</span>
              </label>
              <input
                data-testid="bpmn-edit-process-define-id-input"
                id="processDefineId"
                type="text"
                pInputText
                formControlName="processDefineId"
                [placeholder]="
                  'bpmn.create.form.process_define_id_placeholder' | translate
                "
                class="w-full"
                [class.p-invalid]="hasFieldError('processDefineId')"
              />
              <div class="h-5">
                @if (hasFieldError("processDefineId")) {
                  <small class="text-red-500 block">
                    {{ getFieldErrorMessage("processDefineId") }}
                  </small>
                }
              </div>
            </div>
          </div>
        </form>
      </div>

      <div class="flex gap-3 ml-6">
        <!-- Cancel Button -->
        <app-button
          [testId]="'bpmn-edit-cancel-btn'"
          [label]="'bpmn.edit.actions.cancel' | translate"
          (emitOnClick)="onCancel()"
          [outlined]="true"
          type="secondary"
        ></app-button>

        <!-- Save Button -->
        <app-button
          [testId]="'bpmn-edit-save-btn'"
          [label]="'bpmn.edit.actions.save' | translate"
          (emitOnClick)="onSave()"
          [disabled]="isSaveDisabled()"
          type="info"
        ></app-button>
      </div>
    </div>
  </div>

  <!-- Main content area -->
  <div class="flex-1 min-h-0 flex overflow-hidden">
    <!-- BPMN Designer Area -->
    <div class="flex-1 flex flex-col min-h-0">
      <!-- BPMN Toolbar -->
      <div class="bg-gray-50 px-4 pt-4 pb-2">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <!-- Clear All Button -->
            <app-button
              [testId]="'bpmn-edit-clear-all-btn'"
              [label]="'bpmn.edit.toolbar.clear_all'"
              [icon]="'clear_all'"
              (emitOnClick)="onClearAll()"
              type="secondary"
              [outlined]="true"
            ></app-button>

            <!-- Import File Button -->
            <app-button
              [testId]="'bpmn-edit-import-file-btn'"
              [label]="'bpmn.edit.toolbar.import_file'"
              [icon]="'upload_file'"
              (emitOnClick)="onImportFile()"
              type="secondary"
              [outlined]="true"
            ></app-button>

            <!-- Export File Button -->
            <app-button
              [testId]="'bpmn-edit-export-file-btn'"
              [label]="'bpmn.edit.toolbar.export_file'"
              [icon]="'download'"
              (emitOnClick)="onExportFile()"
              type="secondary"
              [outlined]="true"
            ></app-button>

            <!-- Export Image Button -->
            <app-button
              [testId]="'bpmn-edit-export-image-btn'"
              [label]="'bpmn.edit.toolbar.export_image'"
              [icon]="'image'"
              (emitOnClick)="onExportImage()"
              type="secondary"
              [outlined]="true"
            ></app-button>
          </div>
        </div>
      </div>

      <!-- BPMN Modeler Container -->
      <div class="flex-1 bg-gray-50 p-4 pt-0">
        <div
          class="h-full bg-white border border-gray-300 rounded-lg overflow-hidden"
        >
          <!-- BPMN Modeler -->
          <div
            #modelerContainer
            class="w-full h-full relative"
            data-testid="bpmn-edit-modeler"
          >
            <!-- Floating Zoom Controls -->
            <div class="absolute bottom-4 right-20 z-10">
              <div
                class="flex items-center gap-1 bg-white/30 border border-gray-300/20 rounded-lg p-1 backdrop-blur-sm"
              >
                <!-- Zoom In Button -->
                <app-button
                  [testId]="'bpmn-edit-zoom-in-btn'"
                  [icon]="'zoom_in'"
                  (emitOnClick)="onZoomIn()"
                  type="secondary"
                  [outlined]="true"
                  [title]="'bpmn.edit.toolbar.zoom_in' | translate"
                ></app-button>

                <!-- Zoom Out Button -->
                <app-button
                  [testId]="'bpmn-edit-zoom-out-btn'"
                  [icon]="'zoom_out'"
                  (emitOnClick)="onZoomOut()"
                  type="secondary"
                  [outlined]="true"
                  [title]="'bpmn.edit.toolbar.zoom_out' | translate"
                ></app-button>

                <!-- Divider -->
                <div class="border-l border-gray-300/30 h-6 mx-1"></div>

                <!-- Fit to Viewport Button -->
                <app-button
                  [testId]="'bpmn-edit-fit-viewport-btn'"
                  [icon]="'fit_screen'"
                  (emitOnClick)="onFitToViewport()"
                  type="secondary"
                  [outlined]="true"
                  [title]="'bpmn.edit.toolbar.fit_viewport' | translate"
                ></app-button>

                <!-- Reset Zoom Button -->
                <app-button
                  [testId]="'bpmn-edit-reset-zoom-btn'"
                  [icon]="'zoom_in_map'"
                  (emitOnClick)="onResetZoom()"
                  type="secondary"
                  [outlined]="true"
                  [title]="'bpmn.edit.toolbar.zoom_reset' | translate"
                ></app-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Properties Panel -->
    <div class="properties-panel w-80 bg-gray-50 pb-4 pr-2">
      <div
        #propertiesPanelContainer
        data-testid="bpmn-edit-properties-panel"
        class="overflow-y-auto bg-gray-50 h-[calc(100%-56px)] mt-[56px] border border-gray-300 rounded-lg"
      ></div>
    </div>
  </div>
</div>
